import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { firestore, storage } from "./firebase_config";
import { Constants } from "../constant";
import type {
  HostedImageDoc,
  HostedImageListItem,
  UploadHostedImageInput,
} from "../types/hostedImage";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";

const ID_PATTERN = /^[a-z0-9]{1,32}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

function randomId(length = 16): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[bytes[i]! % chars.length];
  }
  return out;
}

async function allocateUniqueId(): Promise<string> {
  for (let attempt = 0; attempt < 12; attempt++) {
    const candidate = randomId(16);
    if (!ID_PATTERN.test(candidate)) continue;
    const snap = await getDoc(
      doc(firestore, Constants.IMAGE_HOSTING_COLLECTION, candidate),
    );
    if (!snap.exists()) return candidate;
  }
  throw new Error("Could not allocate an id. Try again.");
}

/** Clamp requested expiry to [1, MAX] days, defaulting to 30 when invalid. */
export function clampExpiryDays(days: number): number {
  if (!Number.isFinite(days))
    return Constants.IMAGE_HOSTING_DEFAULT_EXPIRY_DAYS;
  const rounded = Math.round(days);
  if (rounded < 1) return 1;
  if (rounded > Constants.IMAGE_HOSTING_MAX_EXPIRY_DAYS) {
    return Constants.IMAGE_HOSTING_MAX_EXPIRY_DAYS;
  }
  return rounded;
}

function sanitizeFilename(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem =
    (dot >= 0 ? name.slice(0, dot) : name)
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40) || "image";
  const ext =
    (dot >= 0 ? name.slice(dot + 1) : "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 5) || "bin";
  return `${stem}.${ext}`;
}

function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.URL?.createObjectURL) {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function validateImageFile(file: File): void {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image files are accepted.");
  }
  if (file.size > Constants.IMAGE_HOSTING_MAX_BYTES) {
    throw new Error("Image must be 10 MB or smaller.");
  }
}

function buildShortUrl(id: string): string {
  return `${Constants.DYNAMIC_LINKS_PUBLIC_BASE}${Constants.IMAGE_HOSTING_SHORT_PATH}/${id}`;
}

/** Prefer the short link; fall back to the Storage URL for older uploads. */
export function hostedImageShareUrl(item: HostedImageListItem): string {
  return item.shortUrl ?? item.downloadUrl;
}

/** Upload an image, mint a shareable link, and record its metadata + expiry. */
export async function uploadHostedImage(
  input: UploadHostedImageInput,
  uid: string,
  actor?: ActivityActor | null,
): Promise<HostedImageDoc> {
  validateImageFile(input.file);

  const expiryDays = clampExpiryDays(input.expiryDays);
  const id = await allocateUniqueId();
  const dims = await readImageDimensions(input.file);
  const path = `${Constants.IMAGE_HOSTING_STORAGE_PREFIX}/${uid}/${id}/${sanitizeFilename(
    input.file.name,
  )}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, input.file, { contentType: input.file.type });
  const downloadUrl = await getDownloadURL(ref);

  const expiresAt = Timestamp.fromMillis(Date.now() + expiryDays * DAY_MS);
  const ownerDisplayName = input.ownerDisplayName.trim() || uid;
  const shortUrl = buildShortUrl(id);

  const payload: Record<string, unknown> = {
    id,
    ownerUid: uid,
    ownerDisplayName,
    fileName: input.file.name.slice(0, 200),
    contentType: input.file.type,
    sizeBytes: input.file.size,
    storagePath: path,
    downloadUrl,
    shortUrl,
    createdAt: serverTimestamp(),
    expiresAt,
  };
  if (dims?.width) payload.imageWidth = dims.width;
  if (dims?.height) payload.imageHeight = dims.height;

  try {
    await setDoc(
      doc(firestore, Constants.IMAGE_HOSTING_COLLECTION, id),
      payload,
    );
  } catch (e) {
    await deleteObject(ref).catch((delErr) => {
      console.warn("Could not roll back uploaded image:", delErr);
    });
    throw e;
  }

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.ImageHosting,
      action: "upload",
      resourceType: "hosted_image",
      resourceId: id,
      summary: `Uploaded image "${input.file.name}" (expires in ${expiryDays}d)`,
      actor,
      details: { expiryDays, sizeBytes: input.file.size },
    });
  }

  // Read back so the caller gets resolved server timestamps for the UI.
  const saved = await getDoc(
    doc(firestore, Constants.IMAGE_HOSTING_COLLECTION, id),
  );
  return saved.data() as HostedImageDoc;
}

function sortByCreatedDesc(a: HostedImageDoc, b: HostedImageDoc): number {
  const at = a.createdAt?.toMillis?.() ?? 0;
  const bt = b.createdAt?.toMillis?.() ?? 0;
  return bt - at;
}

/**
 * List the caller's own uploads. Filters by `ownerUid` (matches the security
 * rule) and sorts newest-first in memory so no composite index is required.
 */
export async function listHostedImages(
  uid: string,
): Promise<HostedImageListItem[]> {
  const snap = await getDocs(
    query(
      collection(firestore, Constants.IMAGE_HOSTING_COLLECTION),
      where("ownerUid", "==", uid),
    ),
  );
  const rows: HostedImageListItem[] = [];
  snap.forEach((d) => rows.push(d.data() as HostedImageListItem));
  rows.sort(sortByCreatedDesc);
  return rows;
}

/** Delete an upload now (Storage object + metadata). Owner-only per rules. */
export async function deleteHostedImage(
  item: HostedImageListItem,
  actor?: ActivityActor | null,
): Promise<void> {
  // Best-effort storage delete — don't block metadata removal if it's gone.
  if (item.storagePath) {
    await deleteObject(storageRef(storage, item.storagePath)).catch((e) => {
      console.warn("Could not delete image object:", e);
    });
  }
  await deleteDoc(doc(firestore, Constants.IMAGE_HOSTING_COLLECTION, item.id));

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.ImageHosting,
      action: "delete",
      resourceType: "hosted_image",
      resourceId: item.id,
      summary: `Deleted hosted image "${item.fileName}"`,
      actor,
    });
  }
}
